import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './TicketTableDashboard.css';
import { useTranslation } from 'react-i18next';
import { FaInstagram, FaFacebookMessenger, FaWhatsapp, FaEnvelope, FaGlobe, FaSort, FaSortUp, FaSortDown, FaUser, FaRobot, FaPlusCircle } from 'react-icons/fa';

const TicketTableDashboard = ({ tickets, user, onTicketClick, compact = false, hasFaAccount = false, onCreateTicket, brands = [], selectedBrand = null, onBrandChange = null, pagination, onPageChange }) => {
  console.log('🎨 TicketTableDashboard rendered with brands:', brands);
  console.log('🎯 Selected brand:', selectedBrand);
  console.log('👤 User data:', user);

  const { t } = useTranslation();
  const [sortOrder, setSortOrder] = useState('desc');
  const [sortField, setSortField] = useState('updated_at');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [activeTopicTab, setActiveTopicTab] = useState('Après vente'); // Default to postsales
  const [useDateCreated, setUseDateCreated] = useState(false); // false = updated_at, true = created_at
  const [searchResults, setSearchResults] = useState(null);
  const [searchField, setSearchField] = useState('query'); // New state for search field selection

  const ticketsPerPage = 20;

  const ASSIGNED_TO_ME_TAB = 'Assigned to me'; // New constant for the tab
  const GENERAL_TAB = 'General'; // New constant for EDN users general tab
  const CHATBOT_TAB = 'chatbot'; // New constant for chatbot tab

  // Search field options that match the backend API
  const searchFieldOptions = {
    query: 'searchGeneral',
    tracking_number: 'trackingNumber',
    client_name: 'clientName',
    shop_name: 'shopName',
    reference: 'reference'
  };

  const channelIcons = {
    Instagram: FaInstagram,
    Messenger: FaFacebookMessenger,
    WhatsApp: FaWhatsapp,
    Gmail: FaEnvelope,
    Website: FaGlobe
  };

  const statusOptions = {
    new: 'newTickets',
    automated: 'automatedTickets',
    waiting_client_response: 'waitingClientResponse',
    waiting_carrier_response: 'waitingCarrierResponse',
    waiting_carrier_commercial_response: 'waitingCarrierCommercialResponse',
    npec_yourstore: 'npecYourStore',
    resolved: 'resolvedTickets',
    affected: 'affectedTickets',
    closed: 'closedTickets',
    open: 'openTickets',
    pending: 'pendingTickets',
    return_pending: 'returnPending',
    to_compensate: 'toCompensate',
    processing: 'processing',
    compensation: 'compensation',
    compensation_invoice_made: 'compensationInvoiceMade',
    compensation_in_progress: 'compensationInProgress'
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const sortTickets = (tickets, field, order) => {
    return [...tickets].sort((a, b) => {
      const valA = a[field];
      const valB = b[field];
      if (field === 'updated_at' || field === 'created_at') {
        return order === 'asc' ? new Date(valA) - new Date(valB) : new Date(valB) - new Date(valA);
      }
      if (field === 'sentiment_score') {
        return order === 'asc' ? valA - valB : valB - valA;
      }
      return order === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
    });
  };

  const filterTickets = (tickets) => {
    return tickets.filter(ticket => {
      const statusMatch = statusFilter ? ticket.status === statusFilter : true;
      
      const searchTerm = searchFilter.toLowerCase();
      const searchMatch = searchTerm ? (
        (ticket.company_name?.toLowerCase().includes(searchTerm)) ||
        (ticket.tracking_number?.toString().toLowerCase().includes(searchTerm)) ||
        (ticket.external_ticket_id?.toString().toLowerCase().includes(searchTerm)) ||
        (ticket.metadata?.reference?.toLowerCase().includes(searchTerm))
      ) : true;
      
      let topicMatch = true;
      let brandMatch = true;

      // Brand filtering
      if (selectedBrand && ticket.brand_id) {
        brandMatch = ticket.brand_id === selectedBrand;
      }

      if (hasFaAccount || user?.edn_api_account) {
        const isAssigned = ticket.employee_assigned && ticket.employee_assigned !== "Not Assigned";

        if (activeTopicTab === ASSIGNED_TO_ME_TAB) {
          topicMatch = isAssigned;
        } else if (activeTopicTab === CHATBOT_TAB) {
          // Filter for chatbot topic tickets
          topicMatch = ticket.topic === 'chatbot';
        } else if (hasFaAccount) {
          // Exclude assigned tickets from "Après vente" and "Avant vente" tabs (only for FA accounts)
          topicMatch = ticket.topic === activeTopicTab && !isAssigned;
        } else if (user?.edn_api_account && activeTopicTab === GENERAL_TAB) {
          // For EDN users on General tab, exclude assigned tickets
          topicMatch = !isAssigned;
        }
      }
      
      return statusMatch && searchMatch && topicMatch && brandMatch;
    });
  };

  const handleDateFieldToggle = (e) => {
    const useCreated = e.target.checked;
    setUseDateCreated(useCreated);
    // Update sort field if currently sorting by date
    if (sortField === 'updated_at' || sortField === 'created_at') {
      setSortField(useCreated ? 'created_at' : 'updated_at');
    }
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const getCurrentDateField = () => {
    return useDateCreated ? 'created_at' : 'updated_at';
  };

  const handleSortChange = (field) => {
    // If clicking on date field, use the currently selected date field
    if (field === 'date') {
      field = getCurrentDateField();
    }
    setSortField(field);
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleSearchFieldChange = (e) => {
    setSearchField(e.target.value);
    // Clear search results when changing field
    if (searchFilter.trim() !== '') {
      handleSearchFilterChange({ target: { value: searchFilter } });
    }
  };

  const handleSearchFilterChange = async (e) => {
    const searchTerm = e.target.value;
    setSearchFilter(searchTerm);

    if (searchTerm.trim() === '') {
      setSearchResults(null);
      return;
    }

    try {
      const token = sessionStorage.getItem('access_token');
      const searchParam = searchField === 'query' ? 'query' : searchField;
      const response = await axios.get(`/api/tickets/tickets/search?${searchParam}=${encodeURIComponent(searchTerm)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data.tickets || []);
    } catch (error) {
      console.error('Error searching tickets:', error);
      setSearchResults([]);
    }
  };

  const getSearchPlaceholder = () => {
    switch (searchField) {
      case 'tracking_number':
        return t('searchByTrackingNumber', 'Search by tracking number...');
      case 'client_name':
        return t('searchByClientName', 'Search by client name...');
      case 'shop_name':
        return t('searchByShopName', 'Search by shop name...');
      case 'reference':
        return t('searchByReference', 'Search by reference...');
      default:
        return t('searchGeneral', 'Search across all fields...');
    }
  };

  const getSentimentScoreClass = (score) => {
    if (score <= 25) return 'low';
    if (score <= 60) return 'medium';
    return 'high';
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <FaSort />;
    return sortOrder === 'asc' ? <FaSortUp /> : <FaSortDown />;
  };

  const handleTopicTabChange = (topic) => {
    setActiveTopicTab(topic);
  };

  const handleBrandChange = (brandId) => {
    console.log('🔄 Brand tab clicked:', brandId);
    if (onBrandChange) {
      onBrandChange(brandId);
    }
  };

  const displayedTickets = searchResults !== null ? searchResults : tickets;
  const sortedAndFilteredTickets = sortTickets(filterTickets(displayedTickets), sortField, sortOrder);

  const handlePageChange = (direction) => {
    const currentPage = pagination?.current_page || 1;
    if (direction === 'next' && pagination?.has_next) {
      onPageChange(currentPage + 1);
    } else if (direction === 'prev' && pagination?.has_previous) {
      onPageChange(currentPage - 1);
    }
  };

  return (
    <div className="ticket-table-dashboard">
      {/* Brand tabs - shown only if user has brands */}
      {brands.length > 0 && (
        <div className="brand-tabs">
          <div className="brand-tabs-label">Brands:</div>
          {brands.map(brand => (
            <button
              key={brand.id}
              className={`brand-tab ${selectedBrand === brand.id ? 'active' : ''}`}
              onClick={() => handleBrandChange(brand.id)}
            >
              {brand.name}
            </button>
          ))}
        </div>
      )}

      {(hasFaAccount || user?.edn_api_account) && (
        <div className="topic-tabs">
          {hasFaAccount && (
            <>
              <button 
                className={`topic-tab ${activeTopicTab === 'Après vente' ? 'active' : ''}`}
                onClick={() => handleTopicTabChange('Après vente')}
              >
                {t('postsales', 'Postsales')}
              </button>
              <button 
                className={`topic-tab ${activeTopicTab === 'Avant vente' ? 'active' : ''}`}
                onClick={() => handleTopicTabChange('Avant vente')}
              >
                {t('presales', 'Presales')}
              </button>
            </>
          )}
          {user?.edn_api_account && !hasFaAccount && (
            <button
              className={`topic-tab ${activeTopicTab === GENERAL_TAB ? 'active' : ''}`}
              onClick={() => handleTopicTabChange(GENERAL_TAB)}
            >
              {t('general', 'General')}
            </button>
          )}
          <button
            className={`topic-tab ${activeTopicTab === CHATBOT_TAB ? 'active' : ''}`}
            onClick={() => handleTopicTabChange(CHATBOT_TAB)}
          >
            {t('chatbot', 'Chatbot')}
          </button>
          <button
            className={`topic-tab ${activeTopicTab === ASSIGNED_TO_ME_TAB ? 'active' : ''}`}
            onClick={() => handleTopicTabChange(ASSIGNED_TO_ME_TAB)}
          >
            {t('assignedToMe', 'Assigned to me')}
          </button>
        </div>
      )}
      
      <div className="table-controls">
        <div className="filters-container">
          <div className="status-filter">
            <select
              id="status-select"
              value={statusFilter}
              onChange={handleStatusFilterChange}
            >
              <option value="">{t('all')}</option>
              {Object.entries(statusOptions).map(([status, translationKey]) => (
                <option key={status} value={status}>
                  {t(translationKey)}
                </option>
              ))}
            </select>
          </div>
          <div className="company-filter">
            <div className="search-container">
              <select
                id="search-field-select"
                value={searchField}
                onChange={handleSearchFieldChange}
                className="search-field-select"
              >
                {Object.entries(searchFieldOptions).map(([field, translationKey]) => (
                  <option key={field} value={field}>
                    {t(translationKey, field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' '))}
                  </option>
                ))}
              </select>
              <input
                id="search-input"
                type="text"
                value={searchFilter}
                onChange={handleSearchFilterChange}
                placeholder={getSearchPlaceholder()}
                className="search-input"
              />
            </div>
          </div>
          <div className="date-field-toggle">
            <label className="date-toggle-label">
              <input
                type="checkbox"
                checked={useDateCreated}
                onChange={handleDateFieldToggle}
              />
              <span>{t('useCreatedDate', 'Use Created Date')}</span>
            </label>
          </div>
          {user?.edn_api_account && !hasFaAccount && onCreateTicket && (
            <div className="create-ticket-filter">
              <button 
                onClick={onCreateTicket} 
                className="una-create-ticket-button"
              >
                <FaPlusCircle style={{ marginRight: '8px' }} /> {t('createEdnTicket', 'Create EDN Ticket')}
              </button>
            </div>
          )}
        </div>
        <div className="pagination">
          <button 
            onClick={() => handlePageChange('prev')} 
            disabled={!pagination?.has_previous}
          >
            {t('common.previous')}
          </button>
          <span>{pagination?.current_page || 1} / {pagination?.total_pages || 1}</span>
          <button 
            onClick={() => handlePageChange('next')} 
            disabled={!pagination?.has_next}
          >
            {t('common.next')}
          </button>
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th>{t('channel')}</th>
            <th>{t('subject')}</th>
            <th 
              onClick={() => handleSortChange('sentiment_score')} 
              className="sortable"
            >
              {t('sentimentScore')} <SortIcon field="sentiment_score" />
            </th>
            {hasFaAccount && <th>{t('topic', 'Topic')}</th>}
            <th>{t('carrier')}</th>
            {!hasFaAccount && <th>{t('company')}</th>}
            <th>{t('status')}</th>
            <th 
              onClick={() => handleSortChange('date')} 
              className="sortable"
            >
              {useDateCreated ? t('createdAt') : t('updatedAt')} <SortIcon field={getCurrentDateField()} />
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedAndFilteredTickets.map((ticket) => {
            const ChannelIcon = channelIcons[ticket.channel_name];
            const dateToShow = useDateCreated ? ticket.created_at : ticket.updated_at;
            return (
              <tr key={ticket._id} onClick={() => onTicketClick(ticket._id)}>
                <td className="tertiary-info">
                  <div className="visual-indicator">
                    {ChannelIcon && <ChannelIcon className="channel-icon-dashboard" />}
                    <span>{ticket.channel_name}</span>
                  </div>
                </td>
                <td className="primary-info truncate">{ticket.subject}</td>
                <td>
                  <span className={`sentiment-score ${getSentimentScoreClass(ticket.sentiment_score)}`}>
                    {Math.round(ticket.sentiment_score)}
                  </span>
                </td>
                {hasFaAccount && (
                  <td className="secondary-info">
                    <span className={`topic-badge ${
                      ticket.topic === 'Avant vente' ? 'presales' : 
                      ticket.topic === 'chatbot' ? 'chatbot' : 'postsales'
                    }`}>
                      {ticket.topic === 'Avant vente' ? t('presales', 'Presales') : 
                       ticket.topic === 'chatbot' ? t('chatbot', 'Chatbot') : 
                       t('postsales', 'Postsales')}
                    </span>
                  </td>
                )}
                <td className="secondary-info">{ticket.carrier_name || t('unknown')}</td>
                {!hasFaAccount && <td className="secondary-info">{ticket.company_name || t('unknown')}</td>}
                <td className="centered">
                  <span className={`status-badge ${ticket.status}`}>
                    {t(ticket.status)}
                  </span>
                </td>
                <td className="tertiary-info date-info">{formatDate(dateToShow)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default TicketTableDashboard;